import { useCallback, useEffect, useState } from "react";
import { OrvisPip, hasNative } from "../native.js";

/**
 * Picture-in-picture for the TV player, whichever way the platform does it:
 *   - Android app: the whole activity goes PiP (OrvisPip plugin); the page
 *     gets `<html class="pip">` so CSS shows only the video. Android 12+
 *     also auto-enters PiP when the user leaves the app mid-video.
 *   - iOS / Safari: WebKit's own video presentation mode.
 *   - Other browsers: the standard Picture-in-Picture API.
 * `onDismiss` fires when the user closes the Android PiP window — the video
 * should stop then, not keep playing invisibly.
 */
type WebKitVideo = HTMLVideoElement & {
  webkitSupportsPresentationMode?: (mode: string) => boolean;
  webkitSetPresentationMode?: (mode: string) => void;
};

export function usePictureInPicture(
  el: HTMLMediaElement | null,
  playing: boolean,
  onDismiss: () => void
): { supported: boolean; enter: () => void } {
  const video = el instanceof HTMLVideoElement ? (el as WebKitVideo) : null;
  const [nativeSupported, setNativeSupported] = useState(false);

  useEffect(() => {
    if (!hasNative("OrvisPip")) return;
    void OrvisPip.isSupported().then((r) => setNativeSupported(r.supported)).catch(() => {});
    const handle = OrvisPip.addListener("pipChanged", (e) => {
      document.documentElement.classList.toggle("pip", e.active);
      if (e.dismissed) onDismiss();
    });
    return () => {
      document.documentElement.classList.remove("pip");
      void handle.then((h) => h.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Android 12+: arm auto-enter only while a video is actually playing.
  useEffect(() => {
    if (!nativeSupported) return;
    const size = { width: video?.videoWidth || 16, height: video?.videoHeight || 9 };
    void OrvisPip.setAutoEnter({ enabled: playing && !!video, ...size }).catch(() => {});
    return () => void OrvisPip.setAutoEnter({ enabled: false }).catch(() => {});
  }, [nativeSupported, playing, video]);

  const webkit = !!video?.webkitSupportsPresentationMode?.("picture-in-picture");
  const standard = typeof document !== "undefined" && "pictureInPictureEnabled" in document && document.pictureInPictureEnabled;
  const supported = !!video && (nativeSupported || webkit || standard);

  const enter = useCallback(() => {
    if (!video) return;
    if (nativeSupported) {
      document.documentElement.classList.add("pip");
      void OrvisPip.enter({ width: video.videoWidth || 16, height: video.videoHeight || 9 }).catch(() =>
        document.documentElement.classList.remove("pip")
      );
    } else if (webkit) {
      video.webkitSetPresentationMode?.("picture-in-picture");
    } else if (standard) {
      void video.requestPictureInPicture().catch((err) => console.warn("[pip]", err));
    }
  }, [video, nativeSupported, webkit, standard]);

  return { supported, enter };
}
